import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import { addCompany } from '../../slices/companies/companiesSlice';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { toast } from 'react-toastify';
import { ProgressOverlay } from '../progress-overlay/ProgressOverlay';
import { FormControl, FormHelperText } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { NWClient } from '../../client/NWClient';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { selectCountriesInfo } from '../../slices/countries/countriesSlice';
import { Company } from '../../models/company.model';
import { TextValidator, ValidatorForm } from 'react-material-ui-form-validator';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';

const CompanyCreateForm = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [businessName, setBusinessName] = useState<string>('');
  const [country, setCountry] = useState<{ value: string; title: string } | null>(null);
  const [businessPhoneNumber, setBusinessPhoneNumber] = useState<string>('');
  const [businessRegistrationNumber, setBusinessRegistrationNumber] = useState<string>('');
  const [fieldsErrors, setFieldsErrors] = useState(null);
  const countries = useAppSelector(selectCountriesInfo);
  const countriesOptions = [...countries]
    .filter((el) => el.code !== 'GB')
    .map((el) => ({ value: el.name, title: el.name }));
  const countriesLoading = useAppSelector((state) => state.countriesInfo.status);
  const companyLoadingStatus = useAppSelector((state) => state.companies.status);
  const onlyDigitSpaceRegExp = useMemo(() => {
    return /^[0-9 ]*$/;
  }, []);
  const phoneRegExp = useMemo(() => {
    return /^\+?[\s\d]*$/;
    //return /^\+\d*$/;
  }, []);
  const businessNameRegEx = useMemo(() => {
    return /^[a-zA-Z0-9 &\-]*$/;
  }, []);

  const handleInputsPossibleErrors = (propertyName: string) => {
    if (fieldsErrors && fieldsErrors[propertyName]) {
      const obj = { ...fieldsErrors };
      delete obj[propertyName];
      setFieldsErrors(obj);
    }
  };

  const handleBusinessRegistrationNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    setBusinessRegistrationNumber(e.target.value);
    handleInputsPossibleErrors('business_abn_acn');
  };

  const handleBusinessPhoneNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    setBusinessPhoneNumber(e.target.value);
    handleInputsPossibleErrors('phone_number');
  };

  const handleBusinessNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setBusinessName(e.target.value);
    handleInputsPossibleErrors('name');
  };

  const createCompany = (e: React.SyntheticEvent) => {
    e.preventDefault();
    setLoading(true);
    const data = {
      name: businessName.trim(),
      phone_number: businessPhoneNumber.trim().replace(/\s/g, ''),
      business_abn_acn: businessRegistrationNumber.trim().replace(/\s/g, ''),
      country: country ? country.value : null,
      postal_country: country ? country.value : null,
      physical_country: country ? country.value : null,
    };
    dispatch(addCompany({ data }))
      .unwrap()
      .then((res: Company) => {
        toast.success(t('messages.companyCreated'));
        const id = Number(res.id);
        NWClient.post(
          `business-profile-details/${id}`,
          {
            company: id,
            business_phone_number: businessPhoneNumber.trim().replace(/\s/g, ''),
            business_country: country ? country.value : null,
            registration_number: businessRegistrationNumber.trim().replace(/\s/g, ''),
          },
          false
        ).then(() => {
          NWClient.post(
            `business-profile-bank-details/${id}`,
            { company: id, currency: 'AUD' },
            false
          ).then(() => {
            window.location.reload();
          });
        });
      })
      .catch(() => {
        toast.error(t('messages.errorOccurred'));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    ValidatorForm.addValidationRule('ABNorACNLength', (value) => {
      return (
        onlyDigitSpaceRegExp.test(value.trim()) &&
        (value.trim().replace(/\s/g, '').length === 11 ||
          value.trim().replace(/\s/g, '').length === 9 ||
          value.trim().length === 0)
      );
    });
    return () => {
      ValidatorForm.removeValidationRule('ABNorACNLength');
    };
  }, [businessRegistrationNumber, onlyDigitSpaceRegExp]);

  useEffect(() => {
    ValidatorForm.addValidationRule('phone', (value) => {
      return (
        (phoneRegExp.test(value.trim()) || value === '') &&
        value.trim().replace(/\s/g, '').length < 18
      );
    });

    return () => {
      ValidatorForm.removeValidationRule('phone');
    };
  }, [businessPhoneNumber, phoneRegExp]);

  useEffect(() => {
    ValidatorForm.addValidationRule('valueLength255', (value) => {
      return value.trim().length < 256;
    });

    return () => {
      ValidatorForm.removeValidationRule('valueLength255');
    };
  }, [businessRegistrationNumber]);

  useEffect(() => {
    ValidatorForm.addValidationRule('businessNameRule', (value) => {
      return businessNameRegEx.test(value.trim());
    });

    return () => {
      ValidatorForm.removeValidationRule('businessNameRule');
    };
  }, [businessName, businessNameRegEx]);

  return (
    <>
      {countriesLoading === 'loading' || companyLoadingStatus === 'loading' ? (
        <ProgressOverlay className='position-absolute' />
      ) : (
        <>
          <Box className='company-create-form'>
            <Box className='message-container'>
              <p>{t('messages.finishCompanySetUp')}</p>
            </Box>
            <ValidatorForm onSubmit={createCompany}>
              <div>
                <TextValidator
                  label={t('labels.businessName')}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleBusinessNameChange(e)}
                  variant='outlined'
                  name='businessName'
                  value={businessName}
                  required={countriesOptions.length > 0}
                  autoComplete='off'
                  validators={['required', 'valueLength255', 'businessNameRule']}
                  errorMessages={[
                    t('messages.fieldRequired'),
                    t('messages.maxLength255'),
                    t('messages.businessNameValidationMessage'),
                  ]}
                />
                {Boolean(fieldsErrors?.name) && (
                  <FormHelperText error={true}>{fieldsErrors?.name.join('.')}</FormHelperText>
                )}
              </div>
              <FormControl>
                <Autocomplete
                  id='country'
                  openOnFocus={true}
                  options={countriesOptions}
                  isOptionEqualToValue={(option, value) => option.title === value.title}
                  getOptionLabel={(option) => option.title}
                  value={country}
                  onChange={(
                    event: ChangeEvent<HTMLInputElement>,
                    newValue: {
                      title: string;
                      value: string;
                    } | null
                  ) => {
                    setCountry(newValue);
                  }}
                  renderOption={(props, option) => {
                    const { ...optionProps } = props;
                    return (
                      <li key={option.value} {...optionProps}>
                        {option.title}
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required={countriesOptions.length > 0}
                      variant='standard'
                      label={t('labels.country')}
                      disabled={countriesOptions.length === 0}
                    />
                  )}
                  disabled={countriesOptions.length === 0}
                />
              </FormControl>
              <div>
                <TextValidator
                  label={t('labels.businessPhoneNumber')}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    handleBusinessPhoneNumberChange(e)
                  }
                  name='businessPhoneNumber'
                  validators={['phone', 'required']}
                  errorMessages={[t('messages.enterCorrectPhoneNumber')]}
                  value={businessPhoneNumber}
                  required={countriesOptions.length > 0}
                  autoComplete='off'
                />
                {Boolean(fieldsErrors?.phone_number) && (
                  <FormHelperText error={true}>
                    {fieldsErrors?.phone_number.join('.')}
                  </FormHelperText>
                )}
              </div>
              <div>
                <TextValidator
                  label={
                    <>
                      <span className='me-1'>{t('labels.ABNorACN')}</span>
                      <Tooltip
                        className='flex-shrink-0'
                        title={t('labels.registrationNumberTypes')}
                        placement='top'
                        arrow={true}
                      >
                        <InfoIcon fontSize='small' color='secondary' />
                      </Tooltip>
                    </>
                  }
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    handleBusinessRegistrationNumberChange(e)
                  }
                  name='businessABNorACN'
                  validators={['ABNorACNLength']}
                  value={businessRegistrationNumber}
                  errorMessages={[t('messages.ABNandACNMessage')]}
                  autoComplete='off'
                />
                {Boolean(fieldsErrors?.business_abn_acn) && (
                  <FormHelperText error={true}>
                    {fieldsErrors?.business_abn_acn.join('.')}
                  </FormHelperText>
                )}
              </div>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'flex-start',
                  width: '100%',
                  marginTop: 'auto',
                }}
              >
                <Button
                  variant='contained'
                  className='btn-default btn-action-basic'
                  type='submit'
                  disabled={
                    !businessName ||
                    (!country && countriesOptions.length > 0) ||
                    !businessPhoneNumber
                  }
                >
                  {t('buttons.submit')}
                </Button>
              </Box>
            </ValidatorForm>
          </Box>
          {loading && <ProgressOverlay />}
        </>
      )}
    </>
  );
};

export default CompanyCreateForm;
